import React, { useEffect, useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import supabase from '../../supabase/supabase';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const AdminLayout = ({ children }) => {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setLoading(false);
      if (!session && location.pathname !== '/admin/login') {
        navigate('/admin/login');
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (!session && location.pathname !== '/admin/login') {
        navigate('/admin/login');
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate, location.pathname]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/admin/login');
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-brand-cream">
      <SafeIcon icon={FiIcons.FiLoader} className="w-8 h-8 text-brand-teal animate-spin" />
    </div>
  );

  if (!session && location.pathname !== '/admin/login') return null;

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      {session && (
        <aside className="w-64 bg-brand-navy text-white hidden md:flex flex-col">
          <div className="p-6 border-b border-white/10">
            <Link to="/admin" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-brand-teal rounded flex items-center justify-center font-bold">A</div>
              <span className="font-bold tracking-tight">Admin Console</span>
            </Link>
          </div>
          <nav className="flex-grow p-4 space-y-2">
            <Link 
              to="/admin" 
              className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${location.pathname === '/admin' ? 'bg-brand-teal text-white' : 'hover:bg-white/5 text-gray-400 hover:text-white'}`}
            >
              <SafeIcon icon={FiIcons.FiGrid} />
              <span>Dashboard</span>
            </Link>
            <Link 
              to="/admin/resources" 
              className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${location.pathname.includes('/admin/resources') ? 'bg-brand-teal text-white' : 'hover:bg-white/5 text-gray-400 hover:text-white'}`}
            >
              <SafeIcon icon={FiIcons.FiBookOpen} />
              <span>Resources</span>
            </Link>
          </nav>
          <div className="p-4 border-t border-white/10">
            <button 
              onClick={handleLogout}
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-gray-400 hover:text-red-400 transition-colors w-full"
            >
              <SafeIcon icon={FiIcons.FiLogOut} />
              <span>Sign Out</span>
            </button>
          </div>
        </aside>
      )}

      {/* Main Content */}
      <main className="flex-grow flex flex-col overflow-hidden">
        {session && (
          <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-8">
            <h2 className="font-semibold text-brand-navy">
              {location.pathname === '/admin' ? 'Overview' : 'Resource Management'}
            </h2>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-500">{session.user.email}</span>
              <div className="w-8 h-8 rounded-full bg-brand-teal/10 flex items-center justify-center text-brand-teal font-bold">
                {session.user.email[0].toUpperCase()}
              </div>
            </div>
          </header>
        )}
        <div className="flex-grow p-8 overflow-y-auto">
          {children}
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;